import PropTypes from 'prop-types';
import { Button } from '@mantine/core';
import DetailsModal from '@/components/DetailsModal';
import prepareTranslate from '@/helpers/dictionary';
import { IconPaperclip } from '@tabler/icons-react';
import { downloadFile } from '@/helpers/download';
import { useEffect, useRef, useState } from 'react';
import emailMutations from '@/api/mutitations/email';
import { interpolateTemplate, splitFileName } from '@/helpers/string';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { CUSTOM_MIME_TYPES } from '@/helpers/enums';
import vars from '../../../../config/vars';
import styles from './index.module.css';

const allowedExtensions = Object.values(CUSTOM_MIME_TYPES).flatMap(({ extensions }) => extensions);
const allowedExtensionsLabel = allowedExtensions.join(', ');

const ModalEmailExport = (props) => {
  const t = prepareTranslate();
  const {
    opened,
    onClose,
    onSend,
    onEmailSent,
    filePreview = [],
    subject,
    message,
    email,
    rowsData,
    emailtemplateId,
    verifySendEmailProps = {},
  } = props;

  const [subjectValue, setSubjectValue] = useState(subject);
  const [messageValue, setMessageValue] = useState(message);
  const [templateData, setTemplateData] = useState(null);
  const [emails, setEmails] = useState(email ? [email] : []);
  const [bccEmails, setBccEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [localFilePreview, setLocalFilePreview] = useState(filePreview);
  const [openedModal, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [modalLabel, setModalLabel] = useState(verifySendEmailProps.title);
  const [modalMessage, setModalMessage] = useState(verifySendEmailProps.message);
  const [modalActions, setModalActions] = useState([]);
  const { mutateAsync: fetchTemplate } = emailMutations.useGetTemplate();
  const emailInitializedRef = useRef(false);

  useEffect(() => {
    if (!opened) {
      emailInitializedRef.current = false;
    }
  }, [opened]);

  useEffect(() => {
    setLocalFilePreview(filePreview);
  }, [filePreview]);

  const addEmail = () => {
    const trimmed = emailInput.trim();

    if (trimmed && !emails.includes(trimmed)) {
      setEmails([...emails, trimmed]);
    }
    setEmailInput('');
  };
  const removeEmail = (remove) => {
    setEmails(emails.filter((e) => e !== remove));
  };
  const removeBccEmail = (remove) => {
    setBccEmails(bccEmails.filter((e) => e !== remove));
  };

  const removeFilePreview = (index) => {
    setLocalFilePreview((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const loadTemplate = async () => {
      if (opened && emailtemplateId) {
        try {
          const response = await fetchTemplate({ id: emailtemplateId });
          setTemplateData(response);
          if (response?.recipients?.length > 0) {
            setBccEmails(response.recipients);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('שגיאה בטעינת תבנית מייל');
        }
      }
    };

    loadTemplate();
  }, [opened, emailtemplateId, fetchTemplate]);

  useEffect(() => {
    if (opened) {
      if (rowsData && templateData) {
        const filled = interpolateTemplate(templateData.html, rowsData);
        setMessageValue(filled);

        const subjectfilled = interpolateTemplate(templateData.subject, rowsData);
        setSubjectValue(subjectfilled);

        if (!emailInitializedRef?.current) {
          setEmails([rowsData?.email]);
          emailInitializedRef.current = true;
        }
      }
    }
  }, [opened, templateData, rowsData]);

  const [isEditing, setIsEditing] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const filesToBase64 = (fileList) => Promise.all(
    fileList.map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ fileData: reader.result.split(',')[1], name: file.name });
      reader.readAsDataURL(file);
    })),
  );

  const validateFiles = (fileList) => {
    const maxFileSizeInBytes = vars.maxFileSize * 1024 ** 2;
    const validFiles = [];
    const oversizeNames = [];
    const invalidTypeNames = [];

    fileList.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (file.size > maxFileSizeInBytes) {
        oversizeNames.push(file.name);
      } else if (!allowedExtensions.includes(ext)) {
        invalidTypeNames.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizeNames.length > 0) {
      notifications.show({
        title: t('components.customDropZone.notification.invalidFileSize.title'),
        message: `${t('components.customDropZone.notification.invalidFileSize.message')} ${oversizeNames.join(', ')}`,
        autoClose: false,
      });
    }

    if (invalidTypeNames.length > 0) {
      notifications.show({
        title: t('components.customDropZone.notification.invalidFileFormat.title'),
        message: `${t('components.customDropZone.notification.invalidFileFormat.message')} ${invalidTypeNames.join(', ')}. ${t('components.customDropZone.notification.invalidFileFormat.allow')} ${allowedExtensionsLabel}`,
        autoClose: false,
      });
    }

    return validFiles;
  };

  const handleFileChange = async (uploadFiles) => {
    const selected = Array.from(uploadFiles.target.files);
    const validFiles = validateFiles(selected);
    if (validFiles.length === 0) return;
    const newPreviewFiles = await filesToBase64(validFiles);
    setLocalFilePreview((prev) => [...prev, ...newPreviewFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;
    const validFiles = validateFiles(droppedFiles);
    if (validFiles.length === 0) return;
    const newPreviewFiles = await filesToBase64(validFiles);
    setLocalFilePreview((prev) => [...prev, ...newPreviewFiles]);
  };

  const handleBlur = () => setIsEditing(false);
  const {
    mutate: sendEmail,
  } = emailMutations.useSendEmail();

  const handleSendEmail = () => {
    if (verifySendEmailProps.verifySendingEmail) {
      setModalLabel(verifySendEmailProps.title);
      setModalMessage(verifySendEmailProps.message);
      setModalActions([
        <Button key='send' w={100} onClick={handleSubmit}>
          {t('global.approval')}
        </Button>,
        <Button key='cancel' variant="outline" onClick={closeModal} w={100} >
          {t('components.email.cancel')}
        </Button>,
      ]);
      openModal();
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    closeModal();
    setIsSending(true);
    onSend();
    const formData = new FormData();
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
    const userCookie = cookies.find((cookie) => cookie.startsWith('userInfo=')) || '';
    const userCookieValue = userCookie ? JSON.parse(decodeURIComponent(userCookie.replace('userInfo=', ''))) : {};
    const userName = `${userCookieValue.firstName} ${userCookieValue.lastName}`;
    const relevantEmails = emails.filter((e) => e !== undefined);
    relevantEmails.forEach((relevantEmail) => {
      formData.append('recipients', relevantEmail);
    });

    if (bccEmails?.length > 0) {
      bccEmails.forEach((bccEmail) => {
        formData.append('recipients', bccEmail);
      });
    }

    formData.append('subject', subjectValue);
    formData.append('content', messageValue);
    formData.append('sentBy', userName);

    // Convert localFilePreview array items to Files
    const previewFiles = localFilePreview.map((preview) => new File(
      [preview.fileData],
      preview.name,
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    ));

    const allFiles = [...previewFiles];
    allFiles.forEach((attachment) => {
      formData.append('attachment', attachment);
    });
    sendEmail(formData, {
      onSuccess: () => {
        setIsSending(false);
        setModalLabel(t('components.email.emailSentSuccessfuly'));
        setModalMessage('');
        setModalActions([<Button key='close' w={100} onClick={() => {
          closeModal();
          if (onEmailSent) onEmailSent();
        }}>
          {t('global.close')}
        </Button>]);
        onClose();
        openModal();
      },
      onError: () => {
        setIsSending(false);
        setModalLabel(t('components.email.emailSentFailed'));
        setModalMessage('');
        setModalActions([<Button key='close' w={100} onClick={closeModal}>
          {t('global.close')}
        </Button>]);
        openModal();
      },
    });
  };

  const DetailsModalActions = [
    <Button key='cancel' variant="outline" onClick={onClose} className={styles.cancelButton} w={120} >
      {t('components.email.cancel')}
    </Button>,
    <Button
      key='send'
      w={120}
      onClick={handleSendEmail}
      loading={isSending}
      className={styles.sendButton}>
      {t('components.email.send')}
    </Button>,
  ];

  return (
    <>
        <DetailsModal
          opened={opened}
          close={onClose}
          label={t('components.email.sendEmail')}
          actions ={DetailsModalActions}
        >
          <div
            className={styles.subContainer}
            onClick={() => setIsEditing('subject')}
          >
            <p className={styles.subTitle}>{t('components.email.subject')}</p>
            {isEditing ? (
              <div
                contentEditable
                onBlur={(e) => {
                  const newValue = e.currentTarget.innerHTML;
                  setSubjectValue(newValue);
                  handleBlur();
                }}
                dangerouslySetInnerHTML={{ __html: subjectValue }}
                className={styles.subject}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: subjectValue }}
                className={styles.subject}

              />
            )}
          </div>
          <div className={styles.emailField}>
            <p className={styles.fieldLabel}>{t('components.email.to')}</p>
            <div className={styles.multiEmailWrapper}>
              {emails.map((mail) => (
                <span key={mail} className={styles.emailTag}>
                  {mail}
                  <span className={styles.removeBtn} onClick={() => removeEmail(mail)}>✕</span>
                </span>
              ))}
              {bccEmails.map((mail) => (
                <span key={mail} className={styles.emailTag}>
                  {mail}
                  <span className={styles.removeBtn} onClick={() => removeBccEmail(mail)}>✕</span>
                </span>
              ))}
              <input

                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    addEmail();
                  }
                  if (e.key === 'backSpace') {
                    e.preventDefault();
                    removeEmail();
                  }
                }}
                placeholder="recipient@example.com"
                className={styles.emailInput}
              />
            </div>
          </div>

          <p className={styles.subTitle}>{t('components.email.message')}</p>
          <div
            className={`${styles.messageContainer} ${isDragging ? styles.messageContainerDragging : ''}`}
            onClick={() => setIsEditing('message')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className={styles.dropOverlay}>
                <p className={styles.dropOverlayText}>{t('components.email.dropFiles')}</p>
              </div>
            )}
            {isEditing === 'message' ? (
              <div
                contentEditable
                onBlur={(e) => {
                  const newValue = e.currentTarget.innerHTML;
                  setMessageValue(newValue);
                  setIsEditing(null);
                }}
                dangerouslySetInnerHTML={{ __html: messageValue }}
                className={styles.messageArea}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: messageValue }}
                className={styles.messageArea}
              />
            )}

            <div className={styles.attachmentWrapper}>
              <label className={styles.clipLabel}>
                <IconPaperclip stroke={1.5} size={20} className={styles.clipIcon} />
                <input
                  type="file"
                  multiple
                  accept={allowedExtensions.join(',')}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </label>

              {localFilePreview?.length > 0 && localFilePreview.map((preview, index) => (
                <div
                  key={`preview-${index}`}
                  className={styles.attachment}
                >
                  <span onClick={() => downloadFile(preview.fileData, preview.name)}>
                    {splitFileName(preview.name).baseName}
                  </span>
                  <span
                    className={styles.removeBtn}
                    onClick={() => removeFilePreview(index)}
                  >✕</span>
                </div>
              ))}
            </div>

          </div>
        </DetailsModal>
      <Modal
        opened={openedModal}
        onClose={closeModal}
        label={modalLabel}
        message={modalMessage}
        actions={modalActions}
      >
      </Modal>
    </>
  );
};

ModalEmailExport.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onEmailSent: PropTypes.func,
  columns: PropTypes.array,
  subject: PropTypes.string,
  message: PropTypes.string,
  email: PropTypes.string,
  filePreview: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    fileData: PropTypes.string,
  })),
  rowsData: PropTypes.array,
  emailtemplateId: PropTypes.string,
  verifySendEmailProps: PropTypes.object,
};

export default ModalEmailExport;
